import React, { Fragment, useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Input from '@material-ui/core/Input'
import InputBase from '@material-ui/core/InputBase'
import InputAdornment from '@material-ui/core/InputAdornment'
import ArrowDown from '@material-ui/icons/ArrowDownward'
import ArrowUp from '@material-ui/icons/ArrowUpward'
import LinearProgress from '@material-ui/core/LinearProgress'
import LOG from '../logging/Logger'
import { MAX_LENGTH_TODO_SUMMARY } from '../data/limits'
import TodoListItemEdit from './todos/TodoListItemEdit'
import Popup from './Popup'

const useStyles = makeStyles(theme => ({
  textField: {
    marginTop: theme.spacing(1.5),
    width: '100%'
  }
}))

let lastTodo = null
let todoToCheck = null // not part of state, we only want useEffect to trigger on todos changing

export default ({
  list,
  projectId,
  createTodo,
  todos,
  decrypt,
  getRepeatPattern,
  getProjectsForAreas,
  clientId,
  deleteTodo,
  redirectToProject,
  getProjectById,
  createProject,
  editTodo,
  getAvailableTags,
  getAreas,
  getTagsFromTodo,
  createAttachment,
  deleteAttachment,
  editAttachment,
  downloadAttachment,
  getTodoAttachments
}) => {
  LOG.debug('Render: AddItem')

  const classes = useStyles()

  const [newTodo, setNewTodo] = useState('')
  const [addTop, setAddTop] = useState(true)
  const [focus, setFocus] = useState(false)
  const [adding, setAdding] = useState(false)
  const [warning, setWarning] = useState(null)

  const [showTodo, setShowTodo] = useState(false)

  useEffect(() => {
    LOG.debug('Check todo is visible', list, todoToCheck, todos)
    const checkTodo = resolvedTodo => {
      if (resolvedTodo && resolvedTodo.list === list) {
        // This will be triggered in View All, need to disregard other lists
        if (
          todos &&
          todos.findIndex(todoItem => todoItem.id === resolvedTodo.id) < 0
        ) {
          setFocus(false)

          LOG.debug('Todo is not visible, set warning')
          setWarning(
            `New item '${decrypt(
              resolvedTodo.name
            )}' not visible with current filters`
          )
        } else {
          LOG.debug(
            'Todo is visible or does not need checking',
            resolvedTodo,
            todos
          )
        }
        todoToCheck = null
      }
    }

    if (todoToCheck instanceof Promise) {
      todoToCheck.then(resolvedTodo => {
        LOG.debug(
          'resolving todo and check if visible',
          list,
          todoToCheck,
          todos
        )
        todoToCheck = resolvedTodo
        lastTodo = resolvedTodo // Use this if we click on the warning and want to display the todo
        checkTodo(resolvedTodo)
      })
    }
  }, [decrypt, list, todos])

  const handleCreateTodo = async () => {
    setAdding(true)
    try {
      todoToCheck = createTodo(newTodo, list, projectId, addTop)
      setNewTodo('')
      setFocus(true)
    } finally {
      setAdding(false)
    }
  }

  const handleKeyPress = event => {
    if (event.repeat) {
      LOG.warn('Ignoring repeating keypress event')
      return
    }
    if (event.keyCode === 13) {
      handleCreateTodo()
    }
  }

  const AddInput = adding ? InputBase : Input

  return (
    <Fragment>
      <AddInput
        className={classes.textField}
        placeholder='Add...'
        variant='outlined'
        value={newTodo}
        inputRef={input => input && (focus ? input.focus() : input.blur())}
        onChange={e => setNewTodo(e.target.value)}
        onKeyDown={e => handleKeyPress(e)}
        onFocus={e => setFocus(true)}
        onBlur={e => setFocus(false)}
        inputProps={{
          'aria-label': 'add-todo',
          'data-lpignore': 'true',
          maxLength: MAX_LENGTH_TODO_SUMMARY
        }}
        startAdornment={
          <InputAdornment position='start' onClick={e => setAddTop(!addTop)}>
            {addTop && <ArrowUp />}
            {!addTop && <ArrowDown />}
          </InputAdornment>
        }
      />
      {adding && <LinearProgress color='secondary' />}
      {lastTodo && (
        <TodoListItemEdit
          todo={lastTodo}
          open={showTodo}
          onClose={() => setShowTodo(false)}
          hasStatus={true}
          hasList={true}
          decrypt={decrypt}
          getRepeatPattern={getRepeatPattern}
          getProjectsForAreas={getProjectsForAreas}
          clientId={clientId}
          createProject={createProject}
          deleteTodo={deleteTodo}
          redirectToProject={redirectToProject}
          getProjectById={getProjectById}
          editTodo={editTodo}
          getAvailableTags={getAvailableTags}
          getAreas={getAreas}
          createAttachment={createAttachment}
          downloadAttachment={downloadAttachment}
          editAttachment={editAttachment}
          deleteAttachment={deleteAttachment}
          getTodoAttachments={getTodoAttachments}
          getTagsFromTodo={getTagsFromTodo}
        />
      )}
      <Popup
        open={warning !== null}
        variant='warning'
        onClose={useCallback(() => setWarning(null), [])}
        onCancel={useCallback(() => setWarning(null), [])}
        onClick={useCallback(() => {
          setWarning(null)
          setShowTodo(true)
        }, [])}
        message={warning}
        autoHideDurationOverride={6000}
      />
    </Fragment>
  )
}
